/**
 * Parser do BWS LL NB IoT+LoRa — respostas de leitura conforme "Lista de comandos" do
 * manual v1.0.0 (ex.: "RCM=5", "RIP1=x,y", "RBTR=w,x,y,z").
 */

export namespace BwsLlNb {
  export interface AutoTest {
    DEV: string; // DM_BWS_LL_NB
    SN: string;
    IC: string;
    IM: string;
    FW: string;
    GPS: string;
    GPSf: string;
    MDM: string;
    RSSI: string;
    ACELC: string;
    ACELP: string;
    ID_ACEL: string;
    ID_MEM: string;
    BATT_VOLT: string;
    CHARGER: string;
    TEMP: string;
    RF: string;
  }

  export interface RadioBehavior {
    no_connection_timeout: number;
    stopped_cycle: number;
    moving_cycle: number;
    flags: number;
  }
}

/** Retorna o conteúdo após `PREFIX=` sem espaços, ou undefined se o prefixo não existir. */
const valueAfter = (input: unknown, prefix: string): string | undefined => {
  if (typeof input !== 'string') return undefined;
  const parts = input.split(`${prefix}=`);
  if (parts.length < 2) return undefined;
  const value = parts[1].replace(/\s+/g, '');
  return value.length ? value : undefined;
};

/** Como `valueAfter`, tentando cada prefixo na ordem. */
const valueAfterAny = (input: unknown, prefixes: string[]): string | undefined => {
  for (const prefix of prefixes) {
    const value = valueAfter(input, prefix);
    if (value !== undefined) return value;
  }
  return undefined;
};

const numberAfter = (input: unknown, prefix: string): number | undefined => {
  const value = valueAfter(input, prefix);
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const flagAfter = (input: unknown, prefix: string): boolean | undefined => {
  const value = numberAfter(input, prefix);
  if (value !== 0 && value !== 1) return undefined;
  return value === 1;
};

/**
 * Separa "host,porta". O manual documenta vírgula (RIP1=x,y); aceita também "host:porta"
 * (formato usado pela família NB) para não quebrar caso o firmware responda assim.
 */
const hostAndPort = (value: string | undefined): [string, number] | undefined => {
  if (!value) return undefined;
  const match = value.match(/^(.+)[,:](\d+)$/);
  if (!match) return undefined;
  return [match[1], Number(match[2])];
};

export class BwsLlNbParser {
  static serial(input: string): string | undefined {
    return valueAfter(input, 'RINS');
  }

  static imei(input: string): string | undefined {
    return valueAfter(input, 'RIMEI');
  }

  static iccid(input: string): string | undefined {
    return valueAfter(input, 'ICCID');
  }

  static firmware(input: string): string | undefined {
    return valueAfter(input, 'RFW');
  }

  /**
   * Separa os seriais do NB e do rádio LoRa nas linhas lidas na identificação.
   *
   * O rádio responde ao LRINS via bypass com o mesmo prefixo "RINS=" do NB (sem o
   * "L"), então as duas respostas só se distinguem pela ordem: o RINS é enviado
   * antes do LRINS, logo a 1ª linha "RINS=" é do NB e a 2ª é do LoRa. "LRINS=" segue
   * aceito para firmwares que respondam com o prefixo completo.
   *
   * @param lines - Linhas recebidas do dispositivo, na ordem de chegada.
   * @returns Os seriais lidos; undefined para a resposta que não chegou.
   */
  static identification_serials(lines: string[]): { nb?: string; lora?: string } {
    const serialLines = lines.filter((line) => line.trim().startsWith('RINS='));
    const loraLine = lines.find((line) => line.includes('LRINS=')) ?? serialLines[1];

    return {
      nb: BwsLlNbParser.serial(serialLines[0] ?? ''),
      lora: BwsLlNbParser.serial(loraLine ?? ''),
    };
  }

  /** "RTK=" casa a resposta do rádio ao LRTK com ou sem o "L". */
  static timestamp(input: string): string | undefined {
    return valueAfter(input, 'RTK');
  }

  // Chaves LoRa: "LW..=" é o formato do dump do LRKEYS; "R..=" é a resposta do
  // rádio aos comandos individuais (LRDA, LRDE...), que volta sem o "L".
  static device_address(input: string): string | undefined {
    return valueAfterAny(input, ['RDA', 'LWDA']);
  }

  static device_eui(input: string): string | undefined {
    return valueAfterAny(input, ['RDE', 'LWDE']);
  }

  static application_eui(input: string): string | undefined {
    return valueAfterAny(input, ['RAP', 'LWAP']);
  }

  static application_key(input: string): string | undefined {
    return valueAfterAny(input, ['RAK', 'LWAK']);
  }

  static application_session_key(input: string): string | undefined {
    return valueAfterAny(input, ['RASK', 'LWASK']);
  }

  static network_session_key(input: string): string | undefined {
    return valueAfterAny(input, ['RNK', 'LWNK']);
  }

  /** RIAP=x,y,z — x = APN, y = usuário, z = senha. */
  static apn(input: string) {
    const value = valueAfter(input, 'RIAP');
    if (!value) return undefined;
    const [address, user, password] = value.split(',');
    if (!address || !user) return undefined;
    return password ? { address, user, password } : { address, user };
  }

  /** RIP1=x,y */
  static ip_primary(input: string) {
    const parsed = hostAndPort(valueAfter(input, 'RIP1'));
    return parsed ? { ip: parsed[0], port: parsed[1] } : undefined;
  }

  /** RIP2=x,y */
  static ip_secondary(input: string) {
    const parsed = hostAndPort(valueAfter(input, 'RIP2'));
    return parsed ? { ip: parsed[0], port: parsed[1] } : undefined;
  }

  /** RID1=x,y */
  static dns_primary(input: string) {
    const parsed = hostAndPort(valueAfter(input, 'RID1'));
    return parsed ? { address: parsed[0], port: parsed[1] } : undefined;
  }

  /** RID2=x,y */
  static dns_secondary(input: string) {
    const parsed = hostAndPort(valueAfter(input, 'RID2'));
    return parsed ? { address: parsed[0], port: parsed[1] } : undefined;
  }

  /** RCM — tempo em movimento (min). */
  static data_transmission_moving(input: string): number | undefined {
    return numberAfter(input, 'RCM');
  }

  /** RCW — tempo parado (min). */
  static data_transmission_stopped(input: string): number | undefined {
    return numberAfter(input, 'RCW');
  }

  /** RCE — tempo de report em SOS (seg). */
  static data_transmission_sos(input: string): number | undefined {
    return numberAfter(input, 'RCE');
  }

  /** RCTE — tempo em modo estoque (min). */
  static data_transmission_stock(input: string): number | undefined {
    return numberAfter(input, 'RCTE');
  }

  /** RCD — tempo em modo desabilitado (min). */
  static data_transmission_disabled(input: string): number | undefined {
    return numberAfter(input, 'RCD');
  }

  /** RFGO — GPS em cada ciclo (1/0). */
  static gps_every_cycle(input: string): boolean | undefined {
    return flagAfter(input, 'RFGO');
  }

  /** RCG — tempo aguardando FIX (seg). */
  static gps_fix_timeout(input: string): number | undefined {
    return numberAfter(input, 'RCG');
  }

  /** RFOM — modo de trabalho (1 estoque, 2 normal, 128 desativado). */
  static work_mode(input: string): number | undefined {
    return numberAfter(input, 'RFOM');
  }

  /** RFST — time out de SOS (min). */
  static sos_timeout(input: string): number | undefined {
    return numberAfter(input, 'RFST');
  }

  /** WFST=x — eco do dispositivo confirmando a gravação do time out de SOS (min). */
  static sos_timeout_ack(input: string): number | undefined {
    return numberAfter(input, 'WFST');
  }

  /** RFPO — string de potência (1/0). */
  static power_string(input: string): boolean | undefined {
    return flagAfter(input, 'RFPO');
  }

  /** RFGM — modo SOS (1/0). */
  static sos_mode(input: string): boolean | undefined {
    return flagAfter(input, 'RFGM');
  }

  /** RBTR=w,x,y,z */
  static radio_behavior(input: string): BwsLlNb.RadioBehavior | undefined {
    const value = valueAfter(input, 'RBTR');
    if (!value) return undefined;
    const parts = value.split(',').map(Number);
    if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return undefined;
    const [no_connection_timeout, stopped_cycle, moving_cycle, flags] = parts;
    return { no_connection_timeout, stopped_cycle, moving_cycle, flags };
  }

  /** AUTOTEST=KEY:VALUE,KEY:VALUE,... */
  static auto_test(input: string): BwsLlNb.AutoTest | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('AUTOTEST=');
    if (parts.length < 2) return undefined;
    return parts[1].split(',').reduce((acc, cur) => {
      const [key, value] = cur.split(':');
      acc[key.trim() as keyof BwsLlNb.AutoTest] = value?.trim();
      return acc;
    }, {} as BwsLlNb.AutoTest);
  }
}
